import { Button, Card, Center, Group, Loader, Select, Stack, Text, Title, Image } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { Outlet } from 'react-router-dom'
import { useVersionContext } from '@/context/VersionContext'
import { useClientContext } from '@/hooks/useClientContext'
import { usePermissions } from '@/hooks/usePermissions'

export function TenantVersionGate() {
  const {
    versions,
    selectedVersion,
    setSelectedVersion,
    isLoading: isVersionsLoading,
    isVersionSelected,
  } = useVersionContext()

  const {
    clients,
    isLoadingClients,
    selectedClientId,
    setSelectedClientId,
  } = useClientContext()

  const {
    canSwitchClient,
    canAccessAllClients,
    effectiveClientLabel,
    tenantId,
    clientName,
    isExpert,
  } = usePermissions()

  if (isExpert || isVersionSelected) {
    return <Outlet />
  }

  const isLoading = isVersionsLoading || (canSwitchClient && isLoadingClients)

  const formatVersionLabel = (version: string, isCurrent?: boolean) => {
    const name = version === 'pre-versioning' ? 'Pre-versioning' : version
    return isCurrent ? `${name} (current)` : name
  }

  const versionOptions = versions.map(v => ({
    value: v.version,
    label: formatVersionLabel(v.version, v.isCurrent),
  }))

  const tenantLabel = clientName || tenantId || effectiveClientLabel

  const tenantOptions = [
    ...(canAccessAllClients ? [{ value: '', label: 'Portfolio View' }] : []),
    ...clients.map(c => ({
      value: c.tenant_id,
      label: c.tenant_name?.trim() || c.tenant_id,
    })),
  ]

  const getTenantDisplay = () => {
    if (canSwitchClient) {
      return (
        <Select
          label="Tenant"
          placeholder={canAccessAllClients ? 'Portfolio View' : 'Select tenant'}
          data={tenantOptions}
          value={selectedClientId ?? ''}
          onChange={value => setSelectedClientId(value || null)}
          searchable
          clearable={canAccessAllClients}
          disabled={isLoadingClients}
          size="md"
        />
      )
    }

    return (
      <Stack gap={2}>
        <Text size="sm" fw={500} c="dimmed">Tenant</Text>
        <Text size="lg" fw={600}>{tenantLabel}</Text>
      </Stack>
    )
  }

  return (
    <Center mih="100vh" bg="gray.0">
      <Card shadow="md" padding="xl" radius="md" w={420}>
        <Stack gap="lg">
          <Group gap="sm" justify="center">
            <Image src="/snh-ai-logo.png" alt="SNH AI" h={36} w={36} fit="contain" />
            <Stack gap={0}>
              <Text size="xs" c="dimmed" lh={1}>SNH AI</Text>
              <Title order={4} fw={700} lh={1.2}>Public Records</Title>
            </Stack>
          </Group>

          <Text size="sm" c="dimmed" ta="center">
            Select your workspace to get started.
          </Text>

          {isLoading ? (
            <Center py="xl">
              <Loader size="md" />
            </Center>
          ) : (
            <Stack gap="md">
              {getTenantDisplay()}

              <Select
                label="Model Version"
                placeholder="Select version"
                data={versionOptions}
                value={selectedVersion}
                onChange={setSelectedVersion}
                size="md"
              />

              <Button
                fullWidth
                size="md"
                mt="sm"
                rightSection={<IconArrowRight size={18} />}
                disabled={!selectedVersion}
                onClick={() => {
                  if (selectedVersion) {
                    setSelectedVersion(selectedVersion)
                  }
                }}
              >
                Enter Dashboard
              </Button>
            </Stack>
          )}
        </Stack>
      </Card>
    </Center>
  )
}
